import React from 'react';
import { Box, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';

interface ProjectDetailsTableProps {
    chartMode: 'line' | 'bar';
    setChartMode: (value: 'line' | 'bar') => void;
    activeHead: string;
    handleHeadClick: (value: string) => void;
    details: any;
    lowerDetails: any
}


export const ProjectDetailsTable: React.FC<ProjectDetailsTableProps> = ({ details, lowerDetails, chartMode, setChartMode, activeHead, handleHeadClick }) => {

    const fmt = (val: number) => 
        Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(val ?? 0);
    
    // eslint-disable-next-line no-unreachable
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 4, }}>
            {!lowerDetails?.length && <Card>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ minWidth: 80, p: 1 }}>
                                    <Box display="flex" gap={2}>
                                        <IconButton size="small" onClick={() => setChartMode('line')}>
                                            <ShowChartIcon fontSize="small" color={chartMode === 'line' ? 'primary' : 'inherit'} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => setChartMode('bar')}>
                                            <BarChartIcon fontSize="small" color={chartMode === 'bar' ? 'primary' : 'inherit'} />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                                {['HH', 'MO', 'MT', 'EQ', 'SC'].map(head => (
                                    <TableCell
                                        key={head}
                                        align="right"
                                        sx={{
                                            fontWeight: 'bold',
                                            fontSize: '0.8rem',
                                            cursor: 'pointer',
                                            color: activeHead === head ? 'primary.main' : 'inherit'
                                        }}
                                        onClick={() => handleHeadClick(head)}
                                    >
                                        {head} {head !== 'HH' ? '(S/.)' : ''}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>
                                    <Box display="flex" alignItems="center">
                                        <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#2e86c1', mr: 1 }} />
                                        Programado
                                    </Box>
                                </TableCell>
                                <TableCell align="right">{fmt(details.HH_Programado)}</TableCell>
                                <TableCell align="right">{fmt(details.Costo_HH_Programado)}</TableCell>
                                <TableCell align="right">{fmt(details.Costo_MT_Programado)}</TableCell>
                                <TableCell align="right">{fmt(details.Costo_HM_Programado)}</TableCell>
                                <TableCell align="right">{fmt(details.Costo_SC_Programado)}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    <Box display="flex" alignItems="center">
                                        <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f1c40f', mr: 1 }} />
                                        Valorizado
                                    </Box>
                                </TableCell>
                                <TableCell align="right">{fmt(details.HH_Valorizado)}</TableCell>
                                <TableCell align="right">{fmt(details.Costo_HH_Valorizado)}</TableCell>
                                <TableCell align="right">{fmt(details.Costo_MT_Valorizado)}</TableCell>
                                <TableCell align="right">{fmt(details.Costo_HM_Valorizado)}</TableCell>
                                <TableCell align="right">{fmt(details.Costo_SC_Valorizado)}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    <Box display="flex" alignItems="center">
                                        <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#1abc9c', mr: 1 }} />
                                        Real
                                    </Box>
                                </TableCell>
                                <TableCell align="right">{fmt(details.HH_Real)}</TableCell>
                                <TableCell align="right">{fmt(details.Costo_HH_Real)}</TableCell>
                                <TableCell align="right">{fmt(details.Costo_MT_Real)}</TableCell>
                                <TableCell align="right">{fmt(details.Costo_HM_Real)}</TableCell>
                                <TableCell align="right">{fmt(details.Costo_SC_Real)}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell >Diferencia</TableCell>
                                <TableCell align="right" sx={{ color: details.Diferencia_HH < 0 ? 'error.main' : 'success.main' }}>{fmt(details.Diferencia_HH)}</TableCell>
                                <TableCell align="right" sx={{ color: details.Diferencia_Costo_HH < 0 ? 'error.main' : 'success.main' }}>{fmt(details.Diferencia_Costo_HH)}</TableCell>
                                <TableCell align="right" sx={{ color: details.Diferencia_Costo_MT < 0 ? 'error.main' : 'success.main' }}>{fmt(details.Diferencia_Costo_MT)}</TableCell>
                                <TableCell align="right" sx={{ color: details.Diferencia_Costo_HM < 0 ? 'error.main' : 'success.main' }}>{fmt(details.Diferencia_Costo_HM)}</TableCell>
                                <TableCell align="right" sx={{ color: details.Diferencia_Costo_SC < 0 ? 'error.main' : 'success.main' }}>{fmt(details.Diferencia_Costo_SC)}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>}

            {!!lowerDetails?.length && (
                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <TableContainer sx={{ maxHeight: 300 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ minWidth: 80, p: 1 }}>
                                            <Box display="flex" gap={2}>
                                                <IconButton size="small" onClick={() => setChartMode('line')}>
                                                    <ShowChartIcon fontSize="small" color={chartMode === 'line' ? 'primary' : 'inherit'} />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => setChartMode('bar')}>
                                                    <BarChartIcon fontSize="small" color={chartMode === 'bar' ? 'primary' : 'inherit'} />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                            Unidad
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                            Presupuesto
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                            Programado
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                            Valorizado
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                            Real
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {lowerDetails.map((row: any, i: number) => (
                                        <TableRow key={i} hover>
                                            <TableCell sx={{ fontSize: '0.75rem' }}>
                                                {row.DesInsumo}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem' }}>
                                                {row.Unidad}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right' }}>
                                                {fmt(row.PresupuestoParcial)}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right' }}>
                                                {fmt(row.ProgramadoParcial)}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right' }}>
                                                {fmt(row.ValorizadoParcial)}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right' }}>
                                                {fmt(row.RealParcial)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};